import { GridColDef } from '@mui/x-data-grid';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { atom, useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ActionButtons from '../../stateless/Common/ActionButtons';
import { COLORS } from '../../../theme/themeConstants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EditStoreOwnerModal from '../../stateless/EditModel';
import AddChoice from './AddChoice';
import {
  useAllChoices,
  useDeleteChoice,
  usePatchChoice,
} from '../../Query/ChoiceQuery';
import React from 'react';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import { useChoiceParams } from './useChoiceParams';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';
export interface ChoiceDataType {
  restaurantDetails: any;
  storeId: any;
  storeType: any;
  id?: any;
  choiceId?: string;
  choiceNameEnglish?: string;
  choiceNameArabic?: string;
  price: number;
  merchantId?: string;
  storeName?: string;
}

export const choiceAtom = atom<ChoiceDataType | null>(null);

interface ManageChoicesTableProps {
  storeId: string;
}

const ManageChoicesTable: React.FC<ManageChoicesTableProps> = () => {
  const { showMessage } = useSnackbar();
  const { t, i18n } = useTranslation();
  const setChoiceAtom = useSetAtom(choiceAtom);
 const { params, dispatch } = useChoiceParams();
 
   const isReady = Boolean(params && Object.keys(params).length > 0);
 
   const memoizedParams = useMemo(() => {
     return isReady ? params : {};
   }, [isReady, params]);
 
   const { data: choiceData, isLoading, refetch } = useAllChoices(memoizedParams);
 
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = '';
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');
  const [, forceUpdate] = useState({});

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const { mutate: patchChoice } = usePatchChoice();
  const { mutate: deleteChoice } = useDeleteChoice();

    const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const filteredRows = useMemo(() => {
    const storeOwners = choiceData?.data || [];
    const searchLower = params.search?.toLowerCase().trim() || '';
    const filtered = storeOwners.filter((row: any) => {
      if (!row) return false;
      const name = (row.storeName || '').toLowerCase();
      const storeType = (row.storeType || '').toLowerCase();
      const choice = (row.choiceNameEnglish || '').toLowerCase();
      return (
        name.includes(searchLower) ||
        storeType.includes(searchLower) ||
        choice.includes(searchLower)
      );
    });
    return filtered.map((row: any, index: any) => {
      if (!row) return { id: `empty-${index}`, sno: index + 1 };
      return {
        ...row,
        id: row.choiceId,
        sno: index + 1 + (params.page - 1) * params.limit,
        name: row.storeName,
        storeType: row.storeType,
        choice: i18n.language === 'ar' ? row.choiceNameArabic : row.choiceNameEnglish,
        price: row.price,
        status: row.status,
      };
    });
  }, [params.search, choiceData, params.page, params.limit, i18n.language]);

  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = choiceData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.choiceId), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, choiceData]);

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice to block', 'error');
    } else if (window.confirm('Are you sure you want to block?')) {
      patchChoice({ id: selectedRows, data: { status: 'blocked' } });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice to unblock', 'error');
    } else if (window.confirm('Are you sure you want to unblock?')) {
      patchChoice({ id: selectedRows, data: { status: 'unblocked' } });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice to delete', 'error');
    } else if (window.confirm('Are you sure you want to delete?')) {
      // deleteChoice(selectedRows);
      deleteChoice(selectedRows, {
      onSuccess: () => {
        setSelectedRows([]);
        showMessage('choice deleted successfully', 'success');
        // Refetch data to update the table
        refetch();
      },
      onError: () => {
        showMessage('Error deleting choice', 'error');
        // If there's an error, we might want to restore the selection
        // setSelectedRows(selectedRows); // Uncomment if you want to restore selection on error
      },
    });
    }
  }, [selectedRows, deleteChoice, showMessage]);

  const handleBlockClickButton = async (id: string) => {
    if (window.confirm('Are you sure you want to block this Choice?')) {
      await patchChoice({ id: [id], data: { status: 'blocked' } });
    }
  };

  const handleUnBlockClickButton = async (id: string) => {
    if (window.confirm('Are you sure you want to unblock this Choice?')) {
      await patchChoice({ id: [id], data: { status: 'unblocked' } });
    }
  };

  const handleDeleteButton = useCallback((id: string) => {
    if (window.confirm('Are you sure you want to delete?')) {
      deleteChoice([id]);
    }
  }, [deleteChoice]);

  const handleAddSubAdmin = () => setOpenAddDialog(true);
  const handleCloseAddDialog = () => setOpenAddDialog(false);
  const handleEditClick = (row: any) => {
    setChoiceAtom(row);
    setOpenEditDialog(true);
  };
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setChoiceAtom(null);
  };

   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

const handlePageChange = (newPage: number) => {
  dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
};

const handlePageSizeChange = (newSize: number) => {
  dispatch({ type: 'SET_LIMIT', payload: newSize });
};

// const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
//   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
// };

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    return (
      <>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={() => { handleClose(); handleEditClick(row); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('choice.editAction')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); const id = typeof row.id === 'string' ? row.id : ''; if (id) handleDeleteButton(id); }}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{t('choice.deleteAction')}</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = useMemo(() => [
    { field: 'sno', headerName: t('choice.columns.sno'), flex: 0.3 },
    { field: 'name', headerName: t('choice.columns.storeName'), flex: 2 },
    { field: 'storeType', headerName: t('choice.columns.storeType'), flex: 2 },
    { field: 'choice', headerName: t('choice.columns.choice'), flex: 2 },
    { field: 'price', headerName: t('choice.columns.price'), flex: 1 },
    { field: 'addedBy', headerName: t('choice.columns.addedBy'), flex: 1 }, 
    {
      field: 'status', headerName: t('choice.columns.status'), flex: 0.5, filterable: false, sortable: false, disableColumnMenu: true,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = typeof params.row.id === 'string' ? params.row.id : '';
        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={() => isBlocked ? handleUnBlockClickButton(id) : handleBlockClickButton(id)}
          >
            {isBlocked ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          </IconButton>
        );
      }
    },
    {
      field: 'actions', headerName: t('choice.columns.actions'), flex: 0.8, filterable: false, sortable: false, disableColumnMenu: true,
      renderCell: (params) => <RowActions row={params.row} />
    },
    // delete column removed in favor of Actions menu
  ], [t, handleEditClick, handleBlockClickButton, handleUnBlockClickButton, handleDeleteButton]);

  return (
    <Box sx={{ p: 2, border: '1px solid #e0e0e0', marginTop: '20px', color: '#666' }}>
      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ width: 600, border: '1px solid #ccc', borderRadius: '8px', background: '#fff' }}>
          <TextField
            fullWidth
            placeholder={t('choice.searchPlaceholder')}
            size="medium"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': { height: 40, width: 600, border: 'none' },
              '& fieldset': { border: 'none' },
              '& .MuiInputBase-input': { paddingTop: '10px' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ActionButtons onBlock={handleBlockClick} onUnblock={handleUnBlockClick} onDelete={handleDeleteClick} showBlock={showBlock} showUnblock={showUnblock} showDelete={selectedRows.length > 0} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSubAdmin}
            size="small"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              bgcolor: COLORS.primary.main,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: COLORS.primary.main,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            {t('choice.add')}
          </Button>
        </Box>
      </Box>

      <EditStoreOwnerModal open={openAddDialog} onClose={handleCloseAddDialog} title={t('choice.add')}>
        <AddChoice onCancel={handleCloseAddDialog} />
      </EditStoreOwnerModal>

      <EditStoreOwnerModal open={openEditDialog} onClose={handleCloseEditDialog} title={modalContent === 'add' ? t('choice.add') : t('choice.edit')}>
        <AddChoice onCancel={handleCloseEditDialog} />
      </EditStoreOwnerModal>

      <CommonDataGrid
        key={`datagrid-page-${params.page}-limit-${params.limit}`}
        rows={filteredRows || []}
        columns={columns}
        pageSize={params.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        checkboxSelection
        loading={isLoading}
        page={params.page - 1}
        rowCount={choiceData?.pagination?.totalItems || 0}
        components={{
          NoRowsOverlay: () => <ErrorHandler type="no-data" message={t('choice.noData')} onRetry={() => refetch()} />
        }}
        selectionModel={selectedRows}
        onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
      />
    </Box>
  );
};

export default ManageChoicesTable;
